import AntIcon from "@/components/icons/AntIcon";
import { List } from "@/components/list/List";
import { getApiBaseUrl } from "@/utils/utils";
import { validationConstants, validationFunctions } from "@/utils/validationUtils";
import { App as AntApp, Button, Flex, Input, Space } from "antd";
import debounce from "lodash/debounce";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchDashboardContacts } from "../../../../../common/api/queries";
import { Permission } from "../../../../../common/security/enums";
import { useAuthToken, useCurrentAccount, useHasPermissions } from "../../../../auth/hooks";
import { resolveInstitutionLogo } from "../../../../institution/utils";
import { DashboardContactType } from "../../../enums";
import { DashboardContact } from "../../../types";
import { DashboardContactForm } from "../../forms";
import { DashboardContactCard } from "./components";

export const DashboardContactsWidget = () => {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();

  const token = useAuthToken();
  const userAccount = useCurrentAccount();

  const [searchValue, setSearchValue] = useState<string>();
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [selectedContact, setSelectedContact] = useState<DashboardContact>();

  const hasAdminDashboardPermission = useHasPermissions(Permission.ADMIN_DASHBOARD);

  const { data: dashboardContacts, isFetching } = useSearchDashboardContacts({
    keyword: searchValue?.length ? searchValue : undefined,
  });

  const validateSearch = (keyword: string) => {
    if (validationFunctions.validateSearchKeyword(keyword)) {
      setSearchValue(keyword);
      setPageIndex(1);
    } else {
      message.error(
        t("validation.size", {
          min: validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
          max: validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
        }),
      );
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        validateSearch(keyword);
      }, 500),
    [],
  );

  const handleUpdateClick = (contact: DashboardContact) => {
    setSelectedContact(contact);
    setContactFormOpen(true);
  };

  const handleContactFormCancel = () => {
    setSelectedContact(undefined);
    setContactFormOpen(false);
  };

  const handleSearchChange = useCallback(
    (value: string) => {
      debouncedSearch(value);
    },
    [debouncedSearch],
  );

  return (
    <Flex vertical style={{ height: "100%" }}>
      <Flex justify="space-between" align="end">
        <h2 style={{ fontWeight: 600, fontSize: 28, lineHeight: "36px" }}>{t("dashboard.contacts.card")}</h2>

        <Space style={{ marginBottom: 14 }}>
          <Input.Search
            style={{ width: 250 }}
            loading={isFetching}
            allowClear
            placeholder={t("common.search")}
            onSearch={validateSearch}
            onChange={(event) => handleSearchChange(event.currentTarget.value)}
          />

          {hasAdminDashboardPermission && (
            <Button type="primary" icon={<AntIcon type="plus" />} onClick={() => setContactFormOpen(true)}>
              {t("common.add")}
            </Button>
          )}
        </Space>
      </Flex>

      <div style={{ height: "100%" }}>
        <List
          layout="grid"
          columns={3}
          gap="16px 24px"
          showEmpty
          style={
            dashboardContacts?.data.length
              ? { width: "100%" }
              : {
                  backgroundColor: "white",
                  borderRadius: 18,
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }
          }
          pagination={{
            current: pageIndex,
            pageSize: 6,
            size: "small",
            hideOnSinglePage: true,
            showSizeChanger: false,
            style: { marginTop: -16 },
            onChange: setPageIndex,
          }}
          items={dashboardContacts?.data ?? []}
          renderItem={(item) =>
            item.type === DashboardContactType.INTERNAL ? (
              <DashboardContactCard
                contactId={item.id}
                imageSrc={
                  item.agent?.profilePicture
                    ? `${getApiBaseUrl()}/agents/${item.agent?.id}/profile-picture?auth=${token}&accountId=${
                        userAccount?.id
                      }`
                    : undefined
                }
                name={item.agent?.aggregatedName ?? ""}
                email={item.agent?.email}
                phone={item.agent?.phone}
                description={item.position}
                onUpdate={() => handleUpdateClick(item)}
              />
            ) : (
              <DashboardContactCard
                contactId={item.id}
                imageSrc={item.institution ? resolveInstitutionLogo(item.institution) : undefined}
                name={item.name ?? ""}
                email={item.email}
                phone={item.phone}
                description={item.institution ? `${item.institution.name} | ${item.position}` : item.position}
                onUpdate={() => handleUpdateClick(item)}
              />
            )
          }
        />
      </div>

      <DashboardContactForm contact={selectedContact} open={contactFormOpen} onFormCancel={handleContactFormCancel} />
    </Flex>
  );
};
